import { useCallback, useEffect, useState } from "react";
import {
  createSupplier,
  deactivateSupplier,
  fetchSuppliers,
  updateSupplier,
} from "@/lib/adminSuppliersApi";
import type { Supplier } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";

interface SupplierDraft {
  name: string;
  cnpj: string;
  phone: string;
  notes: string;
}

const EMPTY_DRAFT: SupplierDraft = { name: "", cnpj: "", phone: "", notes: "" };

export default function AdminSuppliers() {
  const account = useApp((state) => state.account);
  // Botões respeitam cada permissão separadamente (o backend é a barreira real; isto é UX).
  const canCreate = hasPermission(account, "SUPRIMENTOS.FORNECEDOR.CRIAR");
  const canEdit = hasPermission(account, "SUPRIMENTOS.FORNECEDOR.EDITAR");
  const canDelete = hasPermission(account, "SUPRIMENTOS.FORNECEDOR.EXCLUIR");

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [draft, setDraft] = useState<SupplierDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchSuppliers();
      setSuppliers(data);
    } catch {
      toast.error("Erro ao carregar fornecedores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditingSupplier(null);
    setDraft(EMPTY_DRAFT);
    setEditorOpen(true);
  };

  const openEdit = (s: Supplier) => {
    setEditingSupplier(s);
    setDraft({
      name: s.name,
      cnpj: s.cnpj ?? "",
      phone: s.phone ?? "",
      notes: s.notes ?? "",
    });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingSupplier(null);
    setDraft(EMPTY_DRAFT);
  };

  const handleSave = async () => {
    if (!draft.name.trim()) { toast.error("Nome é obrigatório"); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        cnpj: draft.cnpj.trim() || undefined,
        phone: draft.phone.trim() || undefined,
        notes: draft.notes.trim() || undefined,
      };
      if (editingSupplier) {
        const updated = await updateSupplier(editingSupplier.id, payload);
        setSuppliers(prev => prev.map(s => s.id === updated.id ? updated : s));
        toast.success("Fornecedor atualizado");
      } else {
        const created = await createSupplier(payload);
        setSuppliers(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success("Fornecedor criado");
      }
      closeEditor();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (s: Supplier) => {
    if (!window.confirm(`Desativar o fornecedor "${s.name}"?`)) return;
    try {
      await deactivateSupplier(s.id);
      setSuppliers(prev => prev.map(x => x.id === s.id ? { ...x, active: false } : x));
      toast.success("Fornecedor desativado");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao desativar");
    }
  };

  const filtered = suppliers.filter(s => {
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q) ||
      (s.cnpj ?? "").toLowerCase().includes(q) ||
      (s.phone ?? "").toLowerCase().includes(q);
  });

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Fornecedores</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Gerencie os fornecedores utilizados nos recebimentos.
            </p>
          </div>
          {canCreate && (
            <Button variant="secondary" onClick={openCreate} className="gap-2">
              <Plus className="h-4 w-4" />
              Novo fornecedor
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2">
          <Search className="h-4 w-4 text-primary-foreground/60" />
          <input
            className="flex-1 bg-transparent text-sm text-primary-foreground placeholder:text-primary-foreground/50 outline-none"
            placeholder="Buscar por nome ou CPF/CNPJ"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
          <Building2 className="h-10 w-10 opacity-30" />
          <p className="text-sm">{search ? "Nenhum fornecedor encontrado" : "Nenhum fornecedor cadastrado"}</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CPF/CNPJ</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(s => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="font-medium">{s.name}</div>
                    {s.notes && (
                      <div className="text-xs text-muted-foreground line-clamp-1 max-w-xs">{s.notes}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.cnpj ?? "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.phone ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={s.active ? "default" : "secondary"}>
                      {s.active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)}>
                          <Pencil className="h-4 w-4" />
                          Editar
                        </Button>
                      )}
                      {canDelete && s.active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => void handleDeactivate(s)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {!canEdit && !canDelete && (
                        <span className="text-xs text-muted-foreground">Somente leitura</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={open => { if (!open) closeEditor(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? "Editar fornecedor" : "Novo fornecedor"}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sup-name">Nome *</Label>
              <Input
                id="sup-name"
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="Ex.: Hortifrúti São João"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sup-cnpj">CPF/CNPJ</Label>
              <Input
                id="sup-cnpj"
                value={draft.cnpj}
                onChange={e => setDraft(d => ({ ...d, cnpj: e.target.value }))}
                placeholder="CNPJ 00.000.000/0000-00 ou CPF 000.000.000-00"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sup-phone">Telefone</Label>
              <Input
                id="sup-phone"
                value={draft.phone}
                onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
                placeholder="(11) 90000-0000"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sup-notes">Observações</Label>
              <Textarea
                id="sup-notes"
                value={draft.notes}
                onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                placeholder="Produtos que costuma fornecer, horários de entrega..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeEditor} disabled={saving}>Cancelar</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingSupplier ? "Salvar" : "Criar fornecedor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
